import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import hubHttpClient from 'hub-http/clients/apiClient';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import PortalIdParser from 'PortalIdParser';
import identity from 'transmute/identity';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import GenericPopoverErrorState from '../../components/GenericPopoverErrorState';
import { insertLink } from '../../utils/documentUtils';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import { quotesApiWrapper } from './QuotesApi';
import QuotesPopoverEmptyState from './QuotesPopoverEmptyState';
import { getFullUrl } from 'hubspot-url-utils';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/globalVarUtil';
import { SalesQuoteIcon } from 'foundations-assets/icons/components/SalesQuoteIcon';

/** Configuration for the Quotes insert capability (sales quote links) */
import { jsx as _jsx } from "react/jsx-runtime";
const QuotesButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertQuotes"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "quotes",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: SalesQuoteIcon
    })
  })
});
const manageQuotesUrl = portalId => `${getFullUrl('app')}/quotes/${portalId}`;
export default function InsertQuotes({
  onInsertContent = identity,
  onOpenPopover = identity,
  apiWrapper = quotesApiWrapper,
  apiClient = hubHttpClient,
  outputType = contentOutputs.RENDERED,
  Button = QuotesButton,
  Header = GenericPopoverHeader,
  contactVid,
  onInsertComplete = () => {},
  PopoverEmptyState = QuotesPopoverEmptyState,
  PopoverErrorState = GenericPopoverErrorState,
  renderedPopoverClassname
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertQuote = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
    onInsertContent();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete, onInsertContent]);
  const apiFunctions = useMemo(() => apiWrapper(apiClient, contactVid), [apiWrapper, apiClient, contactVid]);
  const QuotesPopover = useMemo(() => {
    const decoratorConfig = {
      outputType,
      apiFunctions
    };
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-ignore ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button,
      PopoverErrorState,
      PopoverEmptyState,
      Header,
      renderedPopoverClassname,
      onOpenPopover,
      searchPlaceHolder: 'draftPlugins.quotes.popover.search.searchPlaceholder',
      search: apiFunctions.searchArticles,
      popoverTestKey: 'quotes-popover',
      // @ts-ignore ts-migrate FIXME
      manageUrl: manageQuotesUrl(PortalIdParser.get()),
      titleMessage: 'draftPlugins.quotes.popover.header.insertQuote'
    }), decoratorConfig);
  }, [onInsertContent, onOpenPopover, outputType, apiFunctions, Button, Header, PopoverEmptyState, PopoverErrorState, renderedPopoverClassname]);
  return /*#__PURE__*/_jsx(QuotesPopover, {
    onChange: insertQuote,
    useProsemirror: true
  });
}